import {
	BrokerDetails,
	ChangePasswordPayload,
	ChangePasswordResponse,
	MultiSelectOption,
	UserDetailsPayload,
} from "@dune/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { privateAxios, publicAxios } from "../axios";
import { usersQueryKeys } from "../query-keys/users.query-keys";

const searchUsers = async (query: string): Promise<MultiSelectOption[]> => {
	const { data } = await privateAxios.get<MultiSelectOption[]>(
		`/users/search?query=${query}`,
	);
	return data;
};

export const useSearchUsers = (query: string) => {
	return useQuery({
		queryKey: ["search-users", query],
		queryFn: () => searchUsers(query),
	});
};

export const fetchSearchUsers = async (
	query: string,
): Promise<MultiSelectOption[]> => {
	const { data } = await publicAxios.get<MultiSelectOption[]>("/users/search", {
		params: { query },
	});
	return data;
};

export const getBrokerDetails = async () => {
	const { data } = await privateAxios.get<BrokerDetails>(
		"/users/get-user-and-society-details",
	);
	return data;
};

export const useGetBrokerDetails = (options?: { enabled?: boolean }) => {
	return useQuery({
		queryKey: ["user-details"],
		queryFn: getBrokerDetails,
		enabled: options?.enabled !== false,
	});
};

export const useUserSuggestionsQuery = (query = "") => {
	return useQuery<MultiSelectOption[]>({
		queryKey: ["userSuggestions", query],
		queryFn: () => fetchSearchUsers(query),
	});
};

export const getMe = async () => {
	const { data } = await privateAxios.get<UserDetailsPayload>("/users/me");
	return data;
};

export const useMe = () => {
	const { data, ...rest } = useQuery({
		queryKey: usersQueryKeys.me(),
		queryFn: getMe,
	});
	return { me: data, ...rest };
};

export const useGetUserProfile = () => {
	return useQuery({
		queryKey: usersQueryKeys.profile(),
		queryFn: getMe,
	});
};

export const changePassword = async (
	changePasswordData: ChangePasswordPayload,
): Promise<ChangePasswordResponse> => {
	const { data } = await privateAxios.patch<ChangePasswordResponse>(
		"/users/me/password",
		changePasswordData,
	);
	return data;
};

export const useChangePassword = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: changePassword,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: usersQueryKeys.me() });
		},
	});
};

const getDuneSignatories = async () => {
	const { data } = await privateAxios.get<
		Array<{
			id: number;
			firstname: string;
			lastname: string;
			email: string;
		}>
	>("/users/dune-signatories");
	return data;
};

export const useGetDuneSignatories = () => {
	return useQuery({
		queryKey: usersQueryKeys.duneSignatories(),
		queryFn: getDuneSignatories,
	});
};
